import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { BackButton } from "../../../components/BackButton";
// Removed useSupabaseRealtimeDemo - using localStorage + background sync instead
import { Input } from "../../../components/ui/input";
import { 
  ShoppingBag, 
  Clock, 
  MapPin, 
  DollarSign,
  Eye,
  Search,
  Filter,
  CreditCard,
} from "lucide-react";
import { useDemoV2 } from "@/features/demo/hooks/useDemoV2";
import { DemoOrderDetailsDrawer } from "../components/DemoOrderDetailsDrawer";
import { computeDemoKpis, amountCents, formatCents, getOrderTimestamp, type DemoOrder, type DemoOrderItem } from "@/lib/kpis/demoKpis";

type OrdersPageOrder = DemoOrder & {
  table_number?: string | null;
  notes?: string | null;
  payment_method?: string | null;
  customer_name?: string | null;
  demo_tables?: { table_number?: string | null } | null;
};

const resolveOrderItems = (order: OrdersPageOrder): DemoOrderItem[] => {
  if (Array.isArray(order.items) && order.items.length > 0) {
    return order.items;
  }
  if (Array.isArray(order.order_items) && order.order_items.length > 0) {
    return order.order_items;
  }
  if (Array.isArray(order.demo_order_items) && order.demo_order_items.length > 0) {
    return order.demo_order_items;
  }
  return [];
};

const OriginalOrdersPageV3 = () => {
  console.log('📋 OriginalOrdersPage: Component rendering...');
  
  const { orders: demoOrders = [], ready } = useDemoV2();
  const orders = useMemo<OrdersPageOrder[]>(() => {
    if (!Array.isArray(demoOrders)) {
      return [];
    }
    const typedOrders = demoOrders as OrdersPageOrder[];
    return [...typedOrders].sort((a, b) => {
      const newestA = getOrderTimestamp(a).getTime();
      const newestB = getOrderTimestamp(b).getTime();
      return newestB - newestA;
    });
  }, [demoOrders]);
  const loading = !ready && orders.length === 0;
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedOrder, setSelectedOrder] = useState<OrdersPageOrder | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Note: Event listeners removed - useDemoV2 hook provides reactive data

  const handleOrderClick = (order: OrdersPageOrder) => {
    setSelectedOrder(order);
    setDrawerOpen(true);
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'new': return 'bg-warning text-warning-foreground';
      case 'preparing': return 'bg-primary text-primary-foreground';
      case 'ready': return 'bg-success text-success-foreground';
      case 'served': return 'bg-muted text-muted-foreground';
      case 'cancelled': return 'bg-destructive text-destructive-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getTimeAgo = (date: string) => {
    const minutes = Math.floor((Date.now() - new Date(date).getTime()) / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };


  const searchValue = searchTerm.trim().toLowerCase();
  const targetStatus = statusFilter.toLowerCase();

  const filteredOrders = orders.filter(order => {
    const orderCode = (order.order_code || order.id || '').toLowerCase();
    const tableLabel = order.table_number ? `table ${order.table_number}`.toLowerCase() : 'counter';
    const matchesSearch =
      searchValue.length === 0 ||
      orderCode.includes(searchValue) ||
      tableLabel.includes(searchValue);
    const status = typeof order.status === 'string' ? order.status.toLowerCase() : '';
    const matchesStatus = targetStatus === "all" || status === targetStatus;
    return matchesSearch && matchesStatus;
  });

  const { todayOrdersCount, revenueTodayCents, kitchenQueueCount } = useMemo(() => computeDemoKpis(orders), [orders]);
  const avgOrderCents = todayOrdersCount ? Math.round(revenueTodayCents / todayOrdersCount) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
                <ShoppingBag className="w-8 h-8 text-primary" />
                Orders
              </h1>
              <p className="text-muted-foreground">Manage and track all restaurant orders</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-white rounded-lg p-6 shadow animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton to="/r/demo/dashboard?mode=demo" text="Back to Dashboard" />
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
                <ShoppingBag className="w-8 h-8 text-primary" />
                Orders
              </h1>
              <p className="text-muted-foreground">
                Manage and track all restaurant orders
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="card-elevated">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Orders Today
              </CardTitle>
              <ShoppingBag className="w-4 h-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{todayOrdersCount}</div>
              <p className="text-xs text-muted-foreground">
                Since midnight
              </p>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Revenue Today
              </CardTitle>
              <DollarSign className="w-4 h-4 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCents(revenueTodayCents)}
              </div>
              <p className="text-xs text-muted-foreground">
                Completed orders
              </p>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Orders
              </CardTitle>
              <Clock className="w-4 h-4 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kitchenQueueCount}</div>
              <p className="text-xs text-muted-foreground">
                Currently in progress
              </p>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Avg Order Value
              </CardTitle>
              <CreditCard className="w-4 h-4 text-accent-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCents(avgOrderCents)}
              </div>
              <p className="text-xs text-muted-foreground">
                Per order today
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-primary" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by order code or table..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              <div className="flex gap-2 flex-wrap">
                {['all', 'new', 'preparing', 'ready', 'served', 'cancelled'].map((status) => (
                  <Button
                    key={status}
                    variant={statusFilter === status ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter(status)}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        <div className="space-y-4">
          {filteredOrders.map((order) => {
            const orderItems = resolveOrderItems(order);
            return (
            <Card key={order.id} className="card-elevated hover:shadow-glow transition-all duration-200 cursor-pointer">
              <CardContent className="p-6" onClick={() => handleOrderClick(order)}>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg">{order.order_code || order.id}</h3>
                      <Badge className={getStatusColor(order.status)}>
                        {order.status}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {order.table_number ? `Table ${order.table_number}` : 'Counter'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {order.created_at ? getTimeAgo(order.created_at) : 'Just now'}
                      </span>
                    </div>

                    <div className="space-y-1">
                      {orderItems.slice(0, 3).map((item, index) => {
                        const quantity = typeof item.quantity === "number" && item.quantity > 0 ? item.quantity : 1;
                        const baseCents =
                          item.price_snapshot_cents ??
                          item.price_cents ??
                          (typeof item.price === "number" ? Math.round(item.price * 100) : 0);
                        const lineTotalCents = baseCents * quantity;
                        return (
                          <div key={index} className="flex justify-between items-center text-sm">
                            <span>{quantity}x {item.item_name ?? "Item"}</span>
                            <span className="font-medium">
                              {formatCents(lineTotalCents)}
                            </span>
                          </div>
                        );
                      })}
                      {orderItems.length > 3 && (
                        <div className="text-sm text-muted-foreground">
                          +{orderItems.length - 3} more items
                        </div>
                      )}
                    </div>

                    {order.notes && (
                      <div className="mt-3 p-2 bg-muted rounded-md">
                        <p className="text-sm text-muted-foreground">
                          <strong>Note:</strong> {order.notes}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-3">
                    <div className="text-right">
                      <div className="text-2xl font-bold">
                        {formatCents(amountCents(order))}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        1 guest
                      </div>
                    </div>
                    
                    <Button variant="outline" size="sm">
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>

        {filteredOrders.length === 0 && (
          <Card className="card-elevated">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ShoppingBag className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Orders Found</h3>
              <p className="text-muted-foreground text-center">
                {searchTerm || statusFilter !== "all" 
                  ? "Try adjusting your search or filters." 
                  : "Orders will appear here when customers start placing them."
                }
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <DemoOrderDetailsDrawer
        order={selectedOrder}
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (!open) {
            setSelectedOrder(null);
          }
        }}
      />
    </div>
  );
};

export default OriginalOrdersPageV3;

